/**
 * Normalize a reference option id for Select binding and comparisons.
 * @param {unknown} id
 * @returns {number|null}
 */
export function normalizeReferenceId(id) {
  if (id === null || id === undefined || id === '') return null;
  const n = Number(id);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Case-insensitive trimmed name for duplicate checks.
 * @param {unknown} name
 * @returns {string}
 */
export function normalizeReferenceName(name) {
  return String(name ?? '')
    .trim()
    .toLowerCase();
}

/**
 * Upsert an option into a mutable array by id (string-safe).
 * @param {Array<object>} options
 * @param {object} item
 * @returns {object} the upserted item
 */
export function upsertOption(options, item) {
  if (!Array.isArray(options) || !item) return item;
  const id = normalizeReferenceId(item.id ?? item.value);
  if (id == null) {
    options.push(item);
    return item;
  }
  const index = options.findIndex(
    (o) => String(o?.id ?? o?.value) === String(id)
  );
  const next = { ...item, id };
  if (index === -1) {
    options.push(next);
  } else {
    options[index] = { ...options[index], ...next };
  }
  return next;
}

/**
 * Find an option whose name matches (trim + case-insensitive).
 * @param {Array<object>} options
 * @param {string} name
 * @param {string} [nameKey='name']
 * @returns {object|null}
 */
export function findOptionByNormalizedName(options, name, nameKey = 'name') {
  const target = normalizeReferenceName(name);
  if (!target || !Array.isArray(options)) return null;
  return options.find((o) => normalizeReferenceName(o?.[nameKey]) === target) ?? null;
}

/** UI hints for department-scoped position selects. */
export const POSITION_SELECT_HINTS = {
  noDepartment: 'Select a department first.',
  noPositions: 'No positions in this department yet. Use + to create one.',
};

/**
 * Whether a sub-department/team belongs to the given department.
 * Rows with null department_id are treated as org-wide.
 * @param {number|null} itemId
 * @param {number|null} departmentId
 * @param {Array<object>} list
 * @param {string} [deptKey='department_id']
 * @returns {boolean}
 */
export function belongsToDepartment(itemId, departmentId, list, deptKey = 'department_id') {
  const id = normalizeReferenceId(itemId);
  if (id == null) return true;
  if (!Array.isArray(list)) return false;
  const row = list.find((o) => String(o?.id ?? o?.value) === String(id));
  if (!row) return false;
  const rowDept = normalizeReferenceId(row[deptKey]);
  if (rowDept == null) return true;
  const dept = normalizeReferenceId(departmentId);
  return dept != null && String(rowDept) === String(dept);
}

/**
 * Whether a position belongs to the selected department (strict — excludes global positions).
 * @param {number|null} positionId
 * @param {number|null} departmentId
 * @param {Array<object>} list
 * @returns {boolean}
 */
export function positionBelongsToDepartment(positionId, departmentId, list) {
  const id = normalizeReferenceId(positionId);
  if (id == null) return true;
  const dept = normalizeReferenceId(departmentId);
  if (dept == null) return false;
  if (!Array.isArray(list)) return false;
  const row = list.find((o) => String(o?.id ?? o?.value) === String(id));
  if (!row) return false;
  const rowDept = normalizeReferenceId(row.department_id);
  return rowDept != null && String(rowDept) === String(dept);
}

/**
 * Positions for a department-scoped select (strict match on department_id).
 * @param {Array<object>} positions
 * @param {number|null} departmentId
 * @param {object} [options]
 * @param {number|null} [options.keepPositionId]
 * @param {string} [options.fallbackLabel]
 * @param {object|null} [options.pendingCreated]
 * @param {(id: unknown) => number|null} [options.normalizeId]
 * @param {string} [options.idKey='id']
 * @param {string} [options.nameKey='name']
 * @returns {Array<object>}
 */
export function filterPositionsByDepartment(positions, departmentId, options = {}) {
  const dept = normalizeReferenceId(departmentId);
  if (dept == null) return [];

  const source = Array.isArray(positions) ? positions : [];
  const normalizeId = options.normalizeId ?? normalizeReferenceId;
  const idKey = options.idKey ?? 'id';
  const nameKey = options.nameKey ?? 'name';

  const normalizePosition = (position) => {
    const id = normalizeId(position?.[idKey] ?? position?.id ?? position?.value);
    return {
      ...position,
      [idKey]: id,
      department_id: normalizeReferenceId(position?.department_id),
    };
  };

  let result = source
    .filter((position) => {
      const rowDept = normalizeReferenceId(position?.department_id);
      return rowDept != null && String(rowDept) === String(dept);
    })
    .map(normalizePosition);

  const keepId = normalizeId(options.keepPositionId);
  if (keepId != null && !result.some((position) => normalizeId(position[idKey]) === keepId)) {
    const fallback = source.find(
      (position) => normalizeId(position?.[idKey] ?? position?.id ?? position?.value) === keepId
    );
    if (fallback) {
      result = [normalizePosition(fallback), ...result];
    } else if (options.fallbackLabel) {
      result = [
        {
          [idKey]: keepId,
          [nameKey]: options.fallbackLabel,
          department_id: dept,
        },
        ...result,
      ];
    }
  }

  const pending = options.pendingCreated;
  if (pending) {
    const pendingId = normalizeId(pending?.[idKey] ?? pending?.id ?? pending?.value);
    const pendingDept = normalizeReferenceId(pending?.department_id);
    if (
      pendingId != null &&
      pendingDept != null &&
      String(pendingDept) === String(dept) &&
      !result.some((position) => normalizeId(position[idKey]) === pendingId)
    ) {
      result = [normalizePosition(pending), ...result];
    }
  }

  return result;
}

/**
 * Hint text for a department-scoped position select.
 * @param {number|null|undefined} departmentId
 * @param {Array<object>} options
 * @returns {string}
 */
export function positionSelectHint(departmentId, options) {
  if (!normalizeReferenceId(departmentId)) {
    return POSITION_SELECT_HINTS.noDepartment;
  }
  if (!Array.isArray(options) || options.length === 0) {
    return POSITION_SELECT_HINTS.noPositions;
  }
  return '';
}

/**
 * Team is compatible if it has no department_id or matches draft.department_id.
 * @param {number|null} teamId
 * @param {{ department_id?: number|null }} draft
 * @param {Array<object>} teams
 * @returns {boolean}
 */
export function teamMatchesContext(teamId, draft, teams) {
  return belongsToDepartment(teamId, draft?.department_id ?? null, teams, 'department_id');
}

/**
 * Clear incompatible org FKs after department change.
 * Mutates draft in place.
 * @param {object} draft
 * @param {{ positions?: Array, subDepartments?: Array, teams?: Array }} options
 */
export function clearInvalidDependentReferences(draft, options = {}) {
  if (!draft) return;
  const positions = options.positions ?? [];
  const subDepartments = options.subDepartments ?? [];
  const teams = options.teams ?? [];

  if (!positionBelongsToDepartment(draft.position_id, draft.department_id, positions)) {
    draft.position_id = null;
  }
  if (!belongsToDepartment(draft.sub_department_id, draft.department_id, subDepartments)) {
    draft.sub_department_id = null;
  }
  if (!teamMatchesContext(draft.team_id, draft, teams)) {
    draft.team_id = null;
  }
}
